import { Fragment, useMemo, useState, type ReactNode } from 'react';
import { Card, CardHeader } from '../../../components/ui/Card';
import { Badge } from '../../../components/ui/Badge';
import { Nil, Table, TableFooter, TGroupRow, THead, Th, Td, Tr } from '../../../components/ui/Table';
import { SegmentedControl } from '../../../components/ui/Tabs';
import { EmptyState } from '../../../components/ui/States';
import { plural } from '../../ProjectManagement/formatters';
import type { CampaignOverviewRow } from '../../../api/outreach';
import {
  CONNECTED_TOOLTIP,
  LAST_ACTIVITY_TOOLTIP,
  CampaignStatus,
  Num,
  Rate,
  campaignDisplayName,
  formatDateTime,
  formatRate,
  hasActivity,
  hasLinkedInSuffix,
  withoutLinkedInSuffix,
} from './format';

/** Flat view opens on the campaigns that did most; the rest sit behind one button. */
const TOP_ROWS = 8;

type GroupBy = 'flat' | 'rep';

const GROUP_OPTIONS: readonly { id: GroupBy; label: string }[] = [
  { id: 'flat', label: 'Flat' },
  { id: 'rep', label: 'By rep' },
];

const UNKNOWN_REP = 'No sender activity';

interface Group {
  key: string;
  rows: CampaignOverviewRow[];
}

function groupKey(row: CampaignOverviewRow, by: GroupBy): string {
  if (by === 'rep') return row.rep ?? UNKNOWN_REP;
  return '';
}

function byContacted(a: CampaignOverviewRow, b: CampaignOverviewRow): number {
  return b.funnel.contacted - a.funnel.contacted || a.campaign.name.localeCompare(b.campaign.name);
}

/**
 * Non-signal campaigns, flat (default) or grouped by the rep — the lemlist
 * user — who sends most of their activity.
 */
export function CampaignsTable({
  rows,
  onOpen,
  title = 'Campaigns',
  warnings,
  header,
  footer,
}: {
  rows: CampaignOverviewRow[];
  onOpen: (campaignId: string) => void;
  title?: string;
  /** Campaign id → messages sent before the invite was accepted, shown as a fix-it on the row. */
  warnings?: ReadonlyMap<string, number>;
  /** Rendered between the title and the table. */
  header?: ReactNode;
  /** A quiet summary line under the table — the funnel totals. */
  footer?: ReactNode;
}) {
  const [groupBy, setGroupBy] = useState<GroupBy>('flat');
  const [showInactive, setShowInactive] = useState(false);
  const [showAll, setShowAll] = useState(false);

  /** Campaigns with no lemlist activity in the selected period sit behind a toggle. */
  const inactiveCount = useMemo(() => rows.filter((r) => !hasActivity(r.funnel)).length, [rows]);
  const shown = useMemo(() => (showInactive ? rows : rows.filter((r) => hasActivity(r.funnel))), [rows, showInactive]);

  // In lemlist usually equals Contacted. When it does on at least half the rows, the column
  // goes and the total is shown under Contacted only where the two differ.
  const inLemlistColumn = useMemo(() => {
    if (shown.length === 0) return false;
    const same = shown.filter((r) => r.leadsInCampaign === r.funnel.contacted).length;
    return same / shown.length < 0.5;
  }, [shown]);
  // Interested is too rare for a column (mostly dots): it rides on the Replied cell when there is one.
  const cols = 5 + (inLemlistColumn ? 1 : 0);
  // A " · LinkedIn" suffix on every name is said once in the header, not per row.
  const allLinkedIn = useMemo(() => shown.length > 0 && shown.every((r) => hasLinkedInSuffix(r.campaign.name)), [shown]);

  const groups = useMemo<Group[]>(() => {
    const map = new Map<string, CampaignOverviewRow[]>();
    for (const row of shown) {
      const key = groupKey(row, groupBy);
      map.set(key, [...(map.get(key) ?? []), row]);
    }
    return [...map.entries()]
      .map(([key, groupRows]) => ({ key, rows: groupRows.sort(byContacted) }))
      .sort((a, b) => {
        // Unknown groups last, so the named ones read first.
        const aUnknown = a.key === UNKNOWN_REP;
        const bUnknown = b.key === UNKNOWN_REP;
        if (aUnknown !== bUnknown) return aUnknown ? 1 : -1;
        const contacted = (g: Group) => g.rows.reduce((n, r) => n + r.funnel.contacted, 0);
        return contacted(b) - contacted(a) || a.key.localeCompare(b.key);
      });
  }, [shown, groupBy]);

  const picker = (
    <div className="flex items-center gap-2">
      {inactiveCount > 0 && (
        <button
          type="button"
          onClick={() => setShowInactive(!showInactive)}
          className="text-body-xs text-text-tertiary underline-offset-2 hover:text-text-secondary hover:underline"
          title="Campaigns with no lemlist activity in the selected period"
        >
          {showInactive ? 'Hide' : 'Show'} {inactiveCount} inactive
        </button>
      )}
      <SegmentedControl options={GROUP_OPTIONS} value={groupBy} onChange={setGroupBy} ariaLabel="Group campaigns" />
    </div>
  );

  return (
    <Card padding="md">
      <CardHeader
        title={title}
        description={
          inLemlistColumn
            ? `Counts are for the selected period; In lemlist is the current total. Last activity counts any lemlist event, replies and accepted invites included, not only touches.${allLinkedIn ? ' All LinkedIn campaigns.' : ''}`
            : `Counts are for the selected period. Where the current total in lemlist differs from Contacted, it shows under it. Last activity counts any lemlist event, replies and accepted invites included, not only touches.${allLinkedIn ? ' All LinkedIn campaigns.' : ''}`
        }
        action={picker}
      />
      {header}
      <div>
        {rows.length === 0 ? (
          <EmptyState compact title="No running or paused campaigns outside the signal plays" />
        ) : shown.length === 0 ? (
          <EmptyState
            compact
            title="No campaign had any activity in this period"
            description={`${plural(inactiveCount, 'inactive campaign')} hidden.`}
          />
        ) : (
          <Table minWidth={760}>
            <THead>
              <tr>
                <Th>Campaign</Th>
                {inLemlistColumn && <Th align="right">In lemlist</Th>}
                <Th align="right">Contacted</Th>
                <Th align="right" title={CONNECTED_TOOLTIP}>Connected</Th>
                <Th align="right">Replied</Th>
                <Th align="right" className="!pr-3" title={LAST_ACTIVITY_TOOLTIP}>Last activity</Th>
              </tr>
            </THead>
            <tbody>
              {groups.map((group) => (
                <Fragment key={group.key || 'all'}>
                  {groupBy !== 'flat' && (
                    <TGroupRow
                      colSpan={cols}
                      label={group.key}
                      summary={`${plural(group.rows.length, 'campaign')} · ${group.rows
                        .reduce((n, r) => n + r.leadsInCampaign, 0)
                        .toLocaleString('en-GB')} leads`}
                    />
                  )}
                  {(groupBy === 'flat' && !showAll ? group.rows.slice(0, TOP_ROWS) : group.rows).map((row) => {
                    const warning = warnings?.get(row.campaign.id) ?? 0;
                    const status = <CampaignStatus campaign={row.campaign} />;
                    return (
                      <Tr key={row.campaign.id} onClick={() => onOpen(row.campaign.id)}>
                        <Td className="min-w-[16rem]">
                          <p className="font-medium text-text-primary" title={row.campaign.name}>
                            {allLinkedIn ? withoutLinkedInSuffix(campaignDisplayName(row.campaign)) : campaignDisplayName(row.campaign)}
                          </p>
                          <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-body-xs text-text-tertiary">
                            {groupBy !== 'rep' && <span>{row.rep ?? 'No sender activity'}</span>}
                            {status}
                            {warning > 0 && (
                              <span title="LinkedIn messages that fired before the person accepted the invite (lemlist: “pending invitation”) in the last full week. Not a failure: the message step comes too soon after the invite; space it out in the sequence.">
                                <Badge variant="warning">Fix sequence · {warning} sent before accept</Badge>
                              </span>
                            )}
                          </div>
                        </Td>
                        {inLemlistColumn && <Td align="right"><Num value={row.leadsInCampaign} /></Td>}
                        <Td align="right">
                          <span className="tabular-nums whitespace-nowrap">
                            <span className="block"><Num value={row.funnel.contacted} strong /></span>
                            {!inLemlistColumn && row.leadsInCampaign !== row.funnel.contacted && (
                              <span className="block text-body-xs text-text-tertiary" title="Leads in the campaign now">
                                {row.leadsInCampaign.toLocaleString('en-GB')} in lemlist
                              </span>
                            )}
                          </span>
                        </Td>
                        <Td align="right">
                          <Rate value={row.funnel.connectRate} n={row.funnel.connected} total={row.funnel.invited} unit="invited" />
                        </Td>
                        <Td align="right">
                          <span className="tabular-nums whitespace-nowrap">
                            <span className="block"><Num value={row.funnel.replied} /></span>
                            {row.funnel.contacted > 0 && (
                              <span className="block text-body-xs text-text-tertiary">
                                {formatRate(row.funnel.replyRate)}
                                {row.funnel.interested > 0 && ` · ${row.funnel.interested} marked interested`}
                              </span>
                            )}
                          </span>
                        </Td>
                        <Td align="right" className="whitespace-nowrap text-text-tertiary !pr-3">
                          {row.funnel.lastActivityAt ? formatDateTime(row.funnel.lastActivityAt) : <Nil />}
                        </Td>
                      </Tr>
                    );
                  })}
                </Fragment>
              ))}
            </tbody>
          </Table>
        )}
        {groupBy === 'flat' && shown.length > TOP_ROWS && (
          <button
            type="button"
            onClick={() => setShowAll((v) => !v)}
            className="mt-3 text-body-xs font-medium text-accent hover:underline"
          >
            {showAll ? `Show the top ${TOP_ROWS}` : `Show ${shown.length - TOP_ROWS} more (by contacted)`}
          </button>
        )}
        {footer && shown.length > 0 && <TableFooter>{footer}</TableFooter>}
      </div>
    </Card>
  );
}
